import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Plus, Save, Upload } from "lucide-react";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";

const QUILL_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, 4, false] }],
    ["bold", "italic", "underline", "strike"],
    [{ color: [] }, { background: [] }],
    [{ list: "ordered" }, { list: "bullet" }, { indent: "-1" }, { indent: "+1" }],
    [{ align: [] }],
    ["blockquote", "code-block"],
    ["link", "image", "video"],
    ["clean"],
  ],
};

export default function BlogManager() {
  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/cms/blog").then(({ data }) => setPosts(data));
  useEffect(() => { load(); }, []);

  const newPost = () => setEditing({ title: "", slug: "", excerpt: "", content: "", cover_image: "", category: "", tags: [], author: "Salon", published: false });
  const save = async () => {
    const payload = { ...editing };
    if (editing.id) await api.put(`/cms/blog/${editing.id}`, payload);
    else await api.post("/cms/blog", payload);
    setEditing(null); load();
  };
  const remove = async (id) => { if (!window.confirm("Löschen?")) return; await api.delete(`/cms/blog/${id}`); load(); };

  const upload = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    const { data } = await api.post("/cms/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    setEditing({ ...editing, cover_image: data.url });
  };

  if (editing) {
    return (
      <div className="p-8 lg:p-12 mv-fade-in max-w-4xl">
        <div className="flex items-end justify-between mb-8">
          <h1 className="font-display text-4xl">{editing.id ? "Beitrag bearbeiten" : "Neuer Beitrag"}</h1>
          <div className="flex gap-2">
            <button onClick={() => setEditing(null)} className="mv-btn-ghost">Abbrechen</button>
            <button data-testid="save-blog-btn" onClick={save} className="mv-btn"><Save className="w-4 h-4" /> Speichern</button>
          </div>
        </div>
        <div className="mv-card p-6 space-y-4">
          <div><label className="block text-xs mb-1">Titel</label><input className="mv-input" value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} /></div>
          <div><label className="block text-xs mb-1">Slug</label><input className="mv-input" value={editing.slug} onChange={(e) => setEditing({ ...editing, slug: e.target.value })} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="block text-xs mb-1">Kategorie</label><input className="mv-input" value={editing.category || ""} onChange={(e) => setEditing({ ...editing, category: e.target.value })} /></div>
            <div><label className="block text-xs mb-1">Autor</label><input className="mv-input" value={editing.author || ""} onChange={(e) => setEditing({ ...editing, author: e.target.value })} /></div>
          </div>
          <div><label className="block text-xs mb-1">Schlagwörter (kommagetrennt)</label><input className="mv-input" value={(editing.tags || []).join(", ")} onChange={(e) => setEditing({ ...editing, tags: e.target.value.split(",").map((t) => t.trim()).filter(Boolean) })} /></div>
          <div>
            <label className="block text-xs mb-1">Beitragsbild</label>
            <div className="flex gap-2 items-center mb-1">
              {editing.cover_image && <img src={editing.cover_image} className="h-12 rounded border" style={{ borderColor: "var(--mv-border)" }} alt="" />}
              <label className="mv-btn-ghost text-xs cursor-pointer"><Upload className="w-3 h-3" /> Hochladen<input type="file" accept="image/*" className="hidden" onChange={upload} /></label>
            </div>
            <input className="mv-input text-xs" value={editing.cover_image || ""} onChange={(e) => setEditing({ ...editing, cover_image: e.target.value })} />
          </div>
          <div><label className="block text-xs mb-1">Kurzbeschreibung</label><textarea className="mv-input min-h-[80px]" value={editing.excerpt || ""} onChange={(e) => setEditing({ ...editing, excerpt: e.target.value })} /></div>
          <div>
            <label className="block text-xs mb-2">Inhalt</label>
            <div className="bg-white rounded-lg" data-testid="blog-quill">
              <ReactQuill theme="snow" value={editing.content || ""} onChange={(v) => setEditing({ ...editing, content: v })} modules={QUILL_MODULES} style={{ minHeight: "300px" }} />
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!editing.published} onChange={(e) => setEditing({ ...editing, published: e.target.checked })} /> Veröffentlichen</label>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 lg:p-12 mv-fade-in">
      <div className="flex items-end justify-between mb-10">
        <div><p className="mv-label mb-2">Magazin</p><h1 className="font-display text-4xl">Blog</h1></div>
        <button data-testid="new-blog-btn" onClick={newPost} className="mv-btn"><Plus className="w-4 h-4" /> Neuer Beitrag</button>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
        {posts.map((p) => (
          <div key={p.id} onClick={() => setEditing(p)} className="mv-card p-5 cursor-pointer" data-testid={`blog-card-${p.slug}`}>
            {p.cover_image && <img src={p.cover_image} className="w-full h-36 object-cover rounded-lg mb-3" alt="" />}
            <p className="text-xs mb-1" style={{ color: "var(--mv-secondary)" }}>{p.category}</p>
            <h3 className="font-display text-lg mb-1">{p.title}</h3>
            <p className="text-xs" style={{ color: "var(--mv-text-muted)" }}>{p.published ? "● Live" : "○ Entwurf"} · {p.author}</p>
            <button onClick={(e) => { e.stopPropagation(); remove(p.id); }} className="text-xs text-red-500 mt-2">Löschen</button>
          </div>
        ))}
      </div>
    </div>
  );
}
